<template>
  <v-autocomplete v-model="selectedItem"
                  :loading="isFetching"
                  :items="items"
                  v-model:search="search"
                  return-object
                  no-filter>
    <template v-slot:item="{ item, index, props }">
      <slot name="item"
            :item="item"
            :index="index"
            :props="props">
      </slot>
    </template>
    <template v-slot:selection="{ item, index }">
      <slot name="selection"
            :item="item"
            :index="index">
      </slot>
    </template>
    <template v-slot:append="">
      <slot name="append"></slot>
    </template>
    <template v-if="pageInfo.hasNextPage"
              v-slot:append-item>
      <v-divider class="mb-2"></v-divider>
      <v-list-item v-intersect="loadNextPage"
                   class="d-flex justify-center">
        <v-progress-circular :size="50"
                             :width="5"
                             color="secondary"
                             indeterminate></v-progress-circular>
      </v-list-item>
    </template>
  </v-autocomplete>
</template>

<script setup lang="ts" generic="T">
import type { TadaDocumentNode } from "gql.tada";
import { isNil } from 'lodash';
import { useQuery } from 'villus';
import { defineModel, ref } from 'vue';

type QueryVariables = {
  first: number;
  after?: string | null;
  search?: string | null;
};

type QueryResult<T> = {
  [key: string]: {
    edges: {
      node: T;
    }[] | null;
    pageInfo: Pick<PageInfo, 'hasNextPage' | 'hasPreviousPage' | 'endCursor'>;
  } | null;
};

type Query = TadaDocumentNode<QueryResult<T>, QueryVariables>;

const props = defineProps({
  variables: {
    type: Object as PropType<QueryVariables>,
    required: false,
    default: { first: 20, search: '' },
  },
  query: {
    type: Object as PropType<Query>,
    required: true,
  },
  items: {
    type: Array<T>,
    required: false,
    default: [],
  },
  tags: {
    type: Array<string>,
    required: false,
    default: undefined,
  },
  computeFilterFunc: {
    type: Function as PropType<(filter: string) => string>,
    required: false,
    default: (filter: string) => filter,
  }
});

const emit = defineEmits(['update:items', 'update:variables']);

const variables = ref(props.variables);
const pageInfo = ref<Pick<PageInfo, 'hasNextPage' | 'hasPreviousPage' | 'endCursor'>>({ hasNextPage: false, hasPreviousPage: false, endCursor: '' });
const items = ref(props.items) as Ref<T[]>;
const selectedItem = defineModel<T>();
const search = ref('');
const filter = computed(() => props.computeFilterFunc(search.value));

const { isFetching } = useQuery({
  query: props.query,
  variables,
  onData: (data) => {
    const connection = data[Object.keys(data)[0]];
    if (!connection?.edges) return;
    pageInfo.value = connection.pageInfo;
    items.value = pageInfo.value.hasPreviousPage
      ? [...items.value, ...connection.edges.map(x => x.node)]
      : [...connection.edges.map(x => x.node)];
    emit('update:items', items.value);
  },
  tags: props.tags
});

watchDebounced(filter, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  variables.value = {
    ...variables.value,
    after: undefined,
    search: isNil(newValue) ? '' : newValue
  };
}, { debounce: 400 });

watch(variables, (newValue, oldValue) => {
  if (newValue === oldValue) {
    return;
  }

  emit('update:variables', variables.value);
});

function loadNextPage(isIntersecting: boolean) {
  if (isIntersecting && pageInfo.value.hasNextPage) {
    variables.value.after = pageInfo.value.endCursor;
  }
}
</script>
